import { useEffect, useId, useState } from "react";
import { Box, Typography } from "@mui/material";
import { CoreModal, CoreSearchField, CoreTextField, coreToast } from "@verity/components/core";
import styles from "./index.module.css";
import Add from "./assets/add-icon.svg?react";
import Selected from "./assets/selected-icon.svg?react";
import { CustomSettingData } from "../../settings-page";
import { THIRD_PARTY_IMAGE_MAP, ThirdPartyPlatform, ThirdPartyPlatformRosters } from "@verity/components";
import i18n from "../../../../i18n";
import { Trans } from "react-i18next";
import { sendPlatformRequestEmailCSM } from "./helpers";
import { Platform } from "@verity/url-matcher";

function OtherThirdPartyPlatformCard({ onClick }: { onClick: () => void }) {
	return (
		<Box component="button" type="button" className={styles.otherPlatformCard} onClick={onClick}>
			<Add />
			<Typography className={styles.otherPlatformsText}>
				{i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.otherPlatforms")}
			</Typography>
		</Box>
	);
}

function ThirdPartyPlatformCard({
	isSelected,
	platform,
	action,
}: {
	isSelected: boolean;
	platform: ThirdPartyPlatform;
	action: (platform: ThirdPartyPlatform) => void;
}) {
	return (
		<Box
			component="button"
			type="button"
			className={`${styles.platformCard} ${isSelected ? styles.platformCardSelected : ""}`}
			onClick={() => action(platform)}
			aria-pressed={isSelected}
		>
			{isSelected && (
				<Selected
					style={{
						position: "absolute",
						top: -10,
						right: -10,
					}}
				/>
			)}

			<Box className={styles.platformCardContent}>
				{/* Platform Icon */}
				<Box className={styles.platformIconWrapper}>
					{(() => {
						const platformKey = platform.platformId as Platform;
						if (platformKey in THIRD_PARTY_IMAGE_MAP) {
							return THIRD_PARTY_IMAGE_MAP[platformKey];
						}
						return null;
					})()}
				</Box>

				{/* Platform Name */}
				<Typography
					sx={{
						fontFamily: "Inter",
						fontWeight: 400,
						fontSize: "14px",
						lineHeight: "100%",
						letterSpacing: "1%",
						textAlign: "center",
						color: "#333333",
					}}
				>
					{platform.platformName}
				</Typography>
			</Box>
		</Box>
	);
}

function ThirdPartyPlatformList({
	platforms,
	isSelected,
	action,
	showRequestPlatformCard = false,
	setShowRequestPlatformModal,
}: {
	platforms: ThirdPartyPlatform[];
	isSelected: boolean;
	action: (platform: ThirdPartyPlatform) => void;
	showRequestPlatformCard?: boolean;
	setShowRequestPlatformModal: React.Dispatch<React.SetStateAction<boolean>>;
}) {
	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "repeat(auto-fill, 200px)",
				gap: "16px",
				width: "100%",
			}}
		>
			{platforms.map((platform) => (
				<ThirdPartyPlatformCard key={platform.platformId} isSelected={isSelected} platform={platform} action={action} />
			))}

			{/* Always last */}
			{showRequestPlatformCard && (
				<OtherThirdPartyPlatformCard
					onClick={() => {
						setShowRequestPlatformModal(true);
					}}
				/>
			)}
		</div>
	);
}

export const IntegrationSection = (props: {
	loadData: ThirdPartyPlatformRosters;
	setting: CustomSettingData<ThirdPartyPlatformRosters>;
	onSettingChange: (value?: ThirdPartyPlatformRosters | undefined) => void;
}) => {
	const [searchText, setSearchText] = useState("");
	const [showRequestPlatformModal, setShowRequestPlatformModal] = useState(false);
	const controlId = useId();

	const [thirdPartyPlatformInfo, setThirdPartyPlatformInfo] = useState<{
		unselectedPlatformRoster: Record<string, ThirdPartyPlatform>;
		selectedPlatformRoster: Record<string, ThirdPartyPlatform>;
	}>(props.setting.value ?? props.loadData);

	// Example data:
	// const mockPlatformData2 = {
	// 	unselectedPlatformRoster: new Map<number, ThirdPartyPlatform>([
	// 		[7, { platformId: 1, name: "Platform 7" }],
	// 		[8, { platformId: 2, name: "Platform 8" }],
	// 		[9, { platformId: 3, name: "Platform 9" }],
	// 		[10, { platformId: 4, name: "Platform 10" }]
	// 	]),
	// 	selectedPlatformRoster: new Map<number, ThirdPartyPlatform>([
	// 		[11, { platformId: 5, name: "Platform 11" }],
	// 		[12, { platformId: 6, name: "Platform 12" }]
	// 	])
	// }

	useEffect(() => {
		props.onSettingChange(thirdPartyPlatformInfo);
	}, [thirdPartyPlatformInfo, props]);

	const searchFilter = searchText.toLowerCase();
	const sortFunction = (a: ThirdPartyPlatform, b: ThirdPartyPlatform) => a.platformName.localeCompare(b.platformName);
	const displayedPlatforms = Object.values(thirdPartyPlatformInfo.unselectedPlatformRoster)
		.filter((platform) => platform.platformName.toLowerCase().includes(searchFilter))
		.sort(sortFunction);
	const displayedSelectedPlatforms = Object.values(thirdPartyPlatformInfo.selectedPlatformRoster)
		.filter((platform) => platform.platformName.toLowerCase().includes(searchFilter))
		.sort(sortFunction);

	const numSelectedPlatforms = Object.keys(thirdPartyPlatformInfo.selectedPlatformRoster).length;

	return (
		<>
			<div style={{ width: "100%", height: "100%", overflowX: "visible" }}>
				<Box
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						width: "100%",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Inter",
							fontWeight: 400,
							fontSize: 14,
							lineHeight: "20px",
							letterSpacing: 0,
							color: "#333333",
						}}
					>
						{i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.description")}
					</Typography>

					<CoreSearchField
						autoComplete="off"
						label={i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.searchPlatformPlaceholder")}
						onSearch={(value) => {
							setSearchText(value);
						}}
						ariaControls={controlId}
						totalResults={displayedSelectedPlatforms.length + displayedPlatforms.length}
						value={searchText}
						style={{
							margin: "0px 100px",
						}}
					/>

					<div style={{ display: "flex", flexDirection: "column", rowGap: "24px", width: "100%" }}>
						{numSelectedPlatforms !== 0 && (
							<div style={{ minWidth: "380px", flex: "1", justifyContent: "center" }} id={controlId}>
								<Typography
									variant="h3"
									style={{
										fontFamily: "Inter",
										fontWeight: 600,
										fontSize: 14,
										lineHeight: "100%",
										letterSpacing: "0.01em",
										textAlign: "start",
										color: "#333333",
										paddingBottom: "10px",
									}}
								>
									Platforms Added ({numSelectedPlatforms})
								</Typography>

								<ThirdPartyPlatformList
									platforms={displayedSelectedPlatforms}
									isSelected={true}
									action={(platform) => {
										setThirdPartyPlatformInfo((info) => {
											const { [platform.platformId]: _removed, ...remainingSelected } = info.selectedPlatformRoster;

											return {
												unselectedPlatformRoster: {
													...info.unselectedPlatformRoster,
													[platform.platformId]: platform,
												},
												selectedPlatformRoster: remainingSelected,
											};
										});
									}}
									setShowRequestPlatformModal={setShowRequestPlatformModal}
								/>
							</div>
						)}

						<div style={{ minWidth: "380px", flex: "1", justifyContent: "center" }}>
							<Typography
								variant="h3"
								style={{
									fontFamily: "Inter",
									fontWeight: 600,
									fontSize: 14,
									lineHeight: "100%",
									letterSpacing: "0.01em",
									textAlign: "start",
									color: "#333333",
									paddingBottom: "10px",
								}}
							>
								Platforms Available
							</Typography>

							<ThirdPartyPlatformList
								platforms={displayedPlatforms}
								isSelected={false}
								action={(platform) => {
									setThirdPartyPlatformInfo((info) => {
										const { [platform.platformId]: _removed, ...remainingUnselected } = info.unselectedPlatformRoster;

										return {
											unselectedPlatformRoster: remainingUnselected,
											selectedPlatformRoster: {
												...info.selectedPlatformRoster,
												[platform.platformId]: platform,
											},
										};
									});
								}}
								showRequestPlatformCard
								setShowRequestPlatformModal={setShowRequestPlatformModal}
							/>
						</div>
					</div>
				</Box>
			</div>
			{showRequestPlatformModal && <RequestPlatformModal setShowRequestPlatformModal={setShowRequestPlatformModal} />}
		</>
	);
};

function RequestPlatformModal({
	setShowRequestPlatformModal,
}: {
	setShowRequestPlatformModal: React.Dispatch<React.SetStateAction<boolean>>;
}) {
	const [platformName, setPlatformName] = useState("");
	const [platformUrl, setPlatformUrl] = useState("");

	const infoTextStyles = {
		fontFamily: "Inter",
		fontWeight: 400,
		fontStyle: "normal",
		fontSize: "14px",
		lineHeight: "150%",
		letterSpacing: "0px",
		color: "#333333",
	};

	return (
		<CoreModal
			open
			onClose={() => setShowRequestPlatformModal(false)}
			title={i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.title")}
			minHeight="min(75%, 525px)"
			minWidth="min(90%, 720px)"
			widthFixed
			heightFixed
			hideHorizontalOverflow
			hideVerticalOverflow
			contentMatchModalHeight
			buttons={[
				{
					text: i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.cancel"),
					color: "secondary",
					onClick: () => setShowRequestPlatformModal(false),
				},
				{
					text: i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.submitRequest"),
					color: "primary",
					onClick: async () => {
						const success = await sendPlatformRequestEmailCSM({ name: platformName, link: platformUrl });

						if (success) {
							coreToast.success(
								i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.successToastMsg"),
								{
									autoClose: 5000,
									draggable: true,
								},
							);
						} else {
							coreToast.error(
								i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.failureToastMsg"),
								{
									autoClose: 5000,
									draggable: true,
								},
							);
						}

						setShowRequestPlatformModal(false);
					},
					disabled: platformName === "" || platformUrl === "",
				},
			]}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "20px",
				}}
			>
				<Typography sx={infoTextStyles}>
					{i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.description")}
				</Typography>

				<Typography sx={infoTextStyles}>
					<Trans
						i18n={i18n}
						i18nKey="portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.noteTxt"
						components={{
							b: <strong />,
						}}
					/>
				</Typography>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						width: "100%",
					}}
				>
					<Typography
						sx={{
							...infoTextStyles,
							color: "#262626",
						}}
					>
						{i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.platformName")}
					</Typography>

					<CoreTextField
						value={platformName}
						onChange={(e) => {
							setPlatformName(e.target.value.trim());
						}}
						placeholder={i18n.t(
							"portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.platformNamePlaceholder",
						)}
						sx={{
							...infoTextStyles,
							color: "#696969",
						}}
					/>
				</Box>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						width: "100%",
					}}
				>
					<Typography
						sx={{
							...infoTextStyles,
							color: "#262626",
						}}
					>
						{i18n.t("portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.url")}
					</Typography>
					<CoreTextField
						value={platformUrl}
						onChange={(e) => {
							setPlatformUrl(e.target.value.trim());
						}}
						placeholder={i18n.t(
							`portal.webpages.settingsRootTSX.thirdPartyIntegration.requestPlatformModal.urlPlaceholder`,
						)}
						sx={{
							...infoTextStyles,
							color: "#696969",
						}}
					/>
				</Box>
			</Box>
		</CoreModal>
	);
}

export default IntegrationSection;
